"use client";

import { useEffect, useState, type RefObject } from "react";

import { prefersReducedMotion } from "@/components/landing/reduced-motion";

/** Client-only helpers shared by the SMB landing's animated pieces. */

export { prefersReducedMotion };

/** The prototype's phone breakpoint: below it the heavier animations
 *  (typing, the pile's fall, inbox ticks) hard-cut or fade instead. */
export const isNarrow = (): boolean => window.matchMedia("(max-width: 600px)").matches;

export const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));

/** Cubic ease-in-out on [0, 1]. */
export const easeInOut = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/** Size a canvas's backing store to its CSS box (device pixel ratio capped at
 *  2: a 3× buffer buys nothing on these drawings) and return the CSS size. */
export const fitCanvas = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
): { width: number; height: number } => {
  const d = Math.min(2, window.devicePixelRatio || 1);
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  canvas.width = Math.round(width * d);
  canvas.height = Math.round(height * d);
  ctx.setTransform(d, 0, 0, d, 0, 0);
  return { width, height };
};

/**
 * A choreography's timers, cleared together. `at` fires once after `ms`;
 * `every` fires `fn(n)` for n = 1…times at `ms` intervals, then stops itself.
 * Effects schedule with these and return `clear` as their cleanup, so a
 * component scrolled away mid-sequence never gets a late setState.
 */
export const schedule = () => {
  const timeouts: number[] = [];
  const intervals: number[] = [];
  return {
    at: (ms: number, fn: () => void): void => {
      timeouts.push(window.setTimeout(fn, ms));
    },
    every: (ms: number, fn: (n: number) => void, times: number): void => {
      const tick = { n: 0 };
      const id = window.setInterval(() => {
        tick.n += 1;
        fn(tick.n);
        if (tick.n >= times) window.clearInterval(id);
      }, ms);
      intervals.push(id);
    },
    clear: (): void => {
      timeouts.forEach((id) => {
        window.clearTimeout(id);
      });
      intervals.forEach((id) => {
        window.clearInterval(id);
      });
    },
  };
};

/**
 * Whether `ref`'s element is intersecting the viewport. Toggles both ways by
 * default, so a reveal keyed on it replays on re-entry — the prototype's
 * behaviour. With `once`, it latches on the first intersection and stops
 * observing: for "has this come near enough to start loading".
 */
export function useInView(
  ref: RefObject<Element | null>,
  threshold = 0.12,
  rootMargin?: string,
  once = false,
): boolean {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.some((e) => e.isIntersecting);
        if (once && !intersecting) return;
        setInView(intersecting);
        if (once) io.disconnect();
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => {
      io.disconnect();
    };
  }, [ref, threshold, rootMargin, once]);
  return inView;
}

/** Swallow the AbortError `play()` rejects with when playback is interrupted
 *  (scrolled away, paused by us) — nothing to recover from. */
export const tryPlay = (video: HTMLVideoElement): void => {
  video.play().catch(() => undefined);
};
